import React from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, Building2, Blocks, Users, MapPin, Settings, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/leads", label: "Leads", icon: Users },
  { to: "/admin/properties", label: "Properties", icon: Building2 },
  { to: "/admin/projects", label: "Projects", icon: Blocks },
  { to: "/admin/locations", label: "Locations", icon: MapPin },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, checking, logout } = useAuth();
  const nav = useNavigate();

  if (checking) return <div className="min-h-screen grid place-items-center bg-muted text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-muted flex">
      <aside className="hidden md:flex w-60 flex-col bg-primary text-primary-foreground fixed inset-y-0">
        <div className="p-6 flex items-center gap-2.5 border-b border-white/10">
          <span className="h-9 w-9 rounded-sm bg-accent text-accent-foreground grid place-items-center font-display text-lg">E</span>
          <span className="font-display text-lg">Admin</span>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`admin-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm font-medium transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
              <l.icon className="h-4 w-4" aria-hidden /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm text-white/70 hover:text-white"><ExternalLink className="h-4 w-4" aria-hidden /> View Site</a>
          <button data-testid="admin-logout" onClick={() => { logout(); nav("/admin/login"); }} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm text-white/70 hover:text-white"><LogOut className="h-4 w-4" aria-hidden /> Logout</button>
        </div>
      </aside>

      <div className="flex-1 md:ml-60">
        {/* mobile top bar */}
        <div className="md:hidden sticky top-0 z-30 bg-primary text-primary-foreground flex items-center justify-between px-4 h-14">
          <span className="font-display">Endeavour Admin</span>
          <button onClick={() => { logout(); nav("/admin/login"); }} aria-label="Logout"><LogOut className="h-5 w-5" aria-hidden /></button>
        </div>
        <div className="md:hidden flex overflow-x-auto hide-scrollbar gap-1 bg-primary/95 px-2 pb-2">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={({ isActive }) => `whitespace-nowrap px-3 py-1.5 rounded-sm text-xs font-medium ${isActive ? "bg-white/15 text-white" : "text-white/70"}`}>{l.label}</NavLink>
          ))}
        </div>
        <main className="p-4 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
